/**
 * Events and summaries management page.
 * Tabbed list (Events / Summaries) with {@link CollapseReplaceWrapper} for create/edit forms.
 */

import * as React from 'react';
import {
  EVENTS_AND_SUMMARIES_TAB_COLORS as TAB_COLORS,
  EVENTS_AND_SUMMARIES_TAB_ICONS as TAB_ICONS,
  EVENTS_AND_SUMMARIES_TAB_LABELS as TAB_LABELS,
  type EventsAndSummariesTab,
} from '@/constants/shared-catalog';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '@/components/forms/Button';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { EventsDataTable } from '@/components/events/EventsDataTable';
import { EventSummariesDataTable } from '@/components/event-summaries/EventSummariesDataTable';
import { CreateEventView } from '@/components/events/CreateEventView';
import { EditEventView } from '@/components/events/EditEventView';
import { EditEventSummaryView } from '@/components/event-summaries/EditEventSummaryView';
import { CreateTutorView } from '@/components/tutors/inperson/CreateTutorView';
import { EditTutorView } from '@/components/tutors/inperson/EditTutorView';
import { useDetailsView } from '@/hooks/useDetailsView';
import { useViewMode } from '@/hooks/useViewMode';
import { ManagementPage } from '@/components/common/ManagementPage';
import { usePageActions } from '@/hooks/usePageActions';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { EVENT_TAB_ID, EVENTS_MENU_ELEMENT_ID } from '@/components/events/event-configs';
import { EVENT_SUMMARY_TAB_ID } from '@/components/event-summaries/event-summary-configs';
import type { TabItem } from '@/components/ui/tabs';

/** Which form is mounted in the collapse wrapper (event, summary, or nested tutor). */
type ActiveForm = 'create' | 'edit' | 'editSummary' | 'createTutor' | 'editTutor' | null;

/** Props for {@link renderEventFormContent}. */
type EventFormContentProps = {
  activeForm: ActiveForm;
  editEventId: string | null;
  editSummaryId: string | null;
  editTutorId: string | null;
  onClose: () => void;
  onEventSuccess: () => void | Promise<void>;
  onTutorSuccess: () => void;
  onRequestCreateTutor: () => void;
};

/** Pick the collapse-wrapper form from `activeForm` (event / summary / tutor). */
function renderEventFormContent({
  activeForm,
  editEventId,
  editSummaryId,
  editTutorId,
  onClose,
  onEventSuccess,
  onTutorSuccess,
  onRequestCreateTutor,
}: EventFormContentProps): React.ReactNode {
  if (activeForm === 'create') {
    return (
      <CreateEventView
        key="events-create"
        onClose={onClose}
        onSuccess={onEventSuccess}
        onRequestCreateTutor={onRequestCreateTutor}
      />
    );
  }
  if (activeForm === 'edit' && editEventId) {
    return (
      <EditEventView
        key={`events-edit-${editEventId}`}
        eventId={editEventId}
        onClose={onClose}
        onSuccess={onEventSuccess}
        onRequestCreateTutor={onRequestCreateTutor}
      />
    );
  }
  if (activeForm === 'editSummary' && editSummaryId) {
    return (
      <EditEventSummaryView
        key={`summary-edit-${editSummaryId}`}
        summaryId={editSummaryId}
        onClose={onClose}
      />
    );
  }
  if (activeForm === 'createTutor') {
    return <CreateTutorView onSuccess={onTutorSuccess} onClose={onClose} />;
  }
  if (activeForm === 'editTutor' && editTutorId) {
    return (
      <EditTutorView
        key={editTutorId}
        tutorUserId={editTutorId}
        onSuccess={onTutorSuccess}
        onClose={onClose}
      />
    );
  }
  return null;
}

/** Events and summaries list with create/edit forms replacing the table. */
export const EventsAndSummariesManagementPage: React.FC = () => {
  const eventsPageActions = usePageActions('events');
  const summaryPageActions = usePageActions('event-summaries');
  const { canCreate, canEditRow, canCancelRow, canManageAttendance, canExportCsv } =
    eventsPageActions;
  const { elements } = useInterfaceElements(EVENTS_MENU_ELEMENT_ID);
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [activeForm, setActiveForm] = React.useState<ActiveForm>(null);
  const [editEventId, setEditEventId] = React.useState<string | null>(null);
  const [editSummaryId, setEditSummaryId] = React.useState<string | null>(null);
  const [editTutorId, setEditTutorId] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState<EventsAndSummariesTab>('events');
  const { viewMode, setViewMode } = useViewMode('events-management');
  const { openRowId, openRow, setOpenRow, closeRow } = useDetailsView();
  const contentRef = React.useRef<HTMLDivElement>(null);

  /** Tabs the user can see; fall back to both if the interface has no tab elements. */
  const accessibleTabs = React.useMemo<EventsAndSummariesTab[]>(() => {
    const tabElements = elements.filter((el) => el.elementType === 'tab');
    if (tabElements.length === 0) return ['events', 'summaries'];
    const result: EventsAndSummariesTab[] = [];
    if (tabElements.some((el) => el.elementId === EVENT_TAB_ID)) result.push('events');
    if (tabElements.some((el) => el.elementId === EVENT_SUMMARY_TAB_ID)) result.push('summaries');
    if (!result.includes('events')) result.unshift('events');
    return result;
  }, [elements]);

  /** If RBAC removes the current tab, snap to the first accessible one. */
  React.useEffect(() => {
    if (!accessibleTabs.includes(activeTab)) {
      setActiveTab(accessibleTabs[0]);
    }
  }, [accessibleTabs, activeTab]);

  /** Deep-link: open create/edit/view via URL params or `location.state.openSummary`. */
  React.useEffect(() => {
    const viewId = searchParams.get('view');
    const editId = searchParams.get('edit');
    const shouldCreate = searchParams.get('create') === 'true';

    const state = location.state as any;
    if (state?.openSummary) {
      const { summaryId, mode } = state.openSummary;
      setActiveTab('summaries');
      if (mode === 'edit') {
        setEditSummaryId(summaryId);
        setActiveForm('editSummary');
        closeRow();
      } else {
        setOpenRow(summaryId);
        setActiveForm(null);
      }
      window.history.replaceState({}, document.title);
      return;
    }

    if (editId) {
      setEditEventId(editId);
      setActiveForm('edit');
      closeRow();
    } else if (viewId) {
      setOpenRow(viewId);
      setActiveForm(null);
      setEditEventId(null);
    } else if (shouldCreate) {
      setActiveForm('create');
      setEditEventId(null);
      closeRow();
    }
  }, [searchParams, location.state, closeRow, setOpenRow]);

  /** Close the form and drop all edit ids so the next open starts clean. */
  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditEventId(null);
    setEditSummaryId(null);
    setEditTutorId(null);
  }, []);

  /** Swap the event form for a nested tutor-create form. */
  const handleRequestCreateTutor = React.useCallback(() => {
    setActiveForm('createTutor');
    setEditTutorId(null);
    closeRow();
  }, [closeRow]);

  /** Close the nested tutor form and refresh tutor / onboarding lists. */
  const handleTutorFormSuccess = React.useCallback(() => {
    setActiveForm(null);
    setEditEventId(null);
    setEditSummaryId(null);
    setEditTutorId(null);
    void queryClient.invalidateQueries({ queryKey: ['users-search', 'tutor'] });
    void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
  }, [queryClient]);

  /** Open the event edit form and close any expanded details row. */
  const handleEditEvent = (eventId: string) => {
    setEditEventId(eventId);
    setActiveForm('edit');
    closeRow();
  };

  /** Scroll the page to the form when it first opens. */
  const prevActiveFormRef = React.useRef(activeForm);
  React.useEffect(() => {
    if (prevActiveFormRef.current !== activeForm && activeForm !== null && contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevActiveFormRef.current = activeForm;
  }, [activeForm]);

  /** Refresh event lists then close the form (await so the table sees new data). */
  const handleEventFormSuccess = React.useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['events'], refetchType: 'all' }),
      queryClient.invalidateQueries({ queryKey: ['event'], refetchType: 'all' }),
    ]);
    setActiveForm(null);
    setEditEventId(null);
    setEditSummaryId(null);
    setEditTutorId(null);
  }, [queryClient]);

  /** Open the summary edit form and close any expanded details row. */
  const handleEditSummary = (summaryId: string) => {
    setEditSummaryId(summaryId);
    setActiveForm('editSummary');
    closeRow();
  };

  /** Expand the summary details row and hide any open form. */
  const handleViewSummary = (summaryId: string) => {
    openRow(summaryId);
    setActiveForm(null);
  };

  /** Stable JSX node for the form — avoids unmounting edit views on re-renders. */
  const formContent = React.useMemo(
    () =>
      renderEventFormContent({
        activeForm,
        editEventId,
        editSummaryId,
        editTutorId,
        onClose: closeForm,
        onEventSuccess: handleEventFormSuccess,
        onTutorSuccess: handleTutorFormSuccess,
        onRequestCreateTutor: handleRequestCreateTutor,
      }),
    [
      activeForm,
      editEventId,
      editSummaryId,
      editTutorId,
      closeForm,
      handleEventFormSuccess,
      handleTutorFormSuccess,
      handleRequestCreateTutor,
    ],
  );

  const tabItems = React.useMemo<TabItem<EventsAndSummariesTab>[]>(
    () =>
      accessibleTabs.map((id) => {
        const Icon = TAB_ICONS[id];
        return {
          id,
          label: TAB_LABELS[id],
          icon: <Icon className="h-4 w-4" />,
          color: TAB_COLORS[id],
        };
      }),
    [accessibleTabs],
  );

  return (
    <ManagementPage
      title="Gestion des Événements"
      subtitle="Créez, modifiez et gérez tous vos événements ainsi que leurs synthèses"
      contentRef={contentRef}
      tabs={
        accessibleTabs.length > 1
          ? {
              items: tabItems,
              activeTab,
              onTabChange: (id: string) => {
                setActiveTab(id as EventsAndSummariesTab);
                closeForm();
                closeRow();
              },
            }
          : undefined
      }
      actions={
        canCreate && activeTab === 'events' ? (
          <Button
            preset="create-entity"
            onClick={() => {
              setActiveForm('create');
              setEditEventId(null);
              closeRow();
            }}
          >
            Créer un événement
          </Button>
        ) : undefined
      }
    >
      <div ref={contentRef}>
        <CollapseReplaceWrapper
          mode="replace"
          isOpen={activeForm !== null}
          formContent={formContent ?? <></>}
        >
          {activeTab === 'events' ? (
            <EventsDataTable
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              expandedRowId={openRowId}
              onToggleRow={openRow}
              onCloseDetails={closeRow}
              onEditEvent={handleEditEvent}
              pageActions={{
                canEditRow,
                canCancelRow,
                canManageAttendance,
                canExportCsv,
                canViewSummary: summaryPageActions.canViewRow,
                canEditSummary: summaryPageActions.canEditRow,
              }}
            />
          ) : (
            <EventSummariesDataTable
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              expandedRowId={openRowId}
              onToggleRow={openRow}
              onCloseDetails={closeRow}
              onEditSummary={handleEditSummary}
              onViewSummary={handleViewSummary}
              pageActions={{
                canEditRow: summaryPageActions.canEditRow,
                canViewRow: summaryPageActions.canViewRow,
                canExportCsv: summaryPageActions.canExportCsv,
              }}
            />
          )}
        </CollapseReplaceWrapper>
      </div>
    </ManagementPage>
  );
};
